import { useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { extractErrorMessage, getStoredToken } from '@/lib/api';
import { Loader2, Receipt, Camera, Check, X as XIcon } from 'lucide-react';
import { tryGetLocation } from '@/lib/construction-offline';

const CATEGORIES = ['Tools', 'Consumables', 'Miscellaneous'] as const;
type Category = (typeof CATEGORIES)[number];

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';
const todayStr = () => new Date().toISOString().slice(0, 10);

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  projectId: string;
}

export function AddExpenseSheet({ open, onClose, onCreated, projectId }: Props) {
  const [category, setCategory] = useState<Category>('Consumables');
  const [amount, setAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(todayStr());
  const [description, setDescription] = useState('');
  const [vendor, setVendor] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setCategory('Consumables');
    setAmount('');
    setExpenseDate(todayStr());
    setDescription('');
    setVendor('');
    setPhotoFile(null);
    setPhotoPreview((p) => { if (p) URL.revokeObjectURL(p); return null; });
    setError(null);
  };

  useEffect(() => {
    if (!photoPreview) return;
    return () => URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  function pickPhoto(f: File) {
    setPhotoFile(f);
    setPhotoPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(f); });
  }
  function clearPhoto() {
    setPhotoFile(null);
    setPhotoPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    if (fileRef.current) fileRef.current.value = '';
  }

  const handleSubmit = async () => {
    setError(null);
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setError('Amount must be greater than 0'); return; }
    if (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(expenseDate)) { setError('Pick a valid date'); return; }
    if (!photoFile) { setError('Please take a photo of the receipt'); return; }

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', photoFile);
      fd.append('category', category);
      fd.append('amount', String(amt));
      fd.append('expenseDate', expenseDate);
      if (description.trim()) fd.append('description', description.trim());
      if (vendor.trim()) fd.append('vendor', vendor.trim());
      const gps = await tryGetLocation();
      if (gps) {
        fd.append('lat', String(gps.lat));
        fd.append('lng', String(gps.lng));
      }
      const jwt = getStoredToken();
      const res = await fetch(
        `${API_BASE}/api/v1/construction-photos/projects/${projectId}/expense`,
        { method: 'POST', headers: jwt ? { Authorization: `Bearer ${jwt}` } : {}, body: fd },
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Upload failed: ${txt || res.statusText}`);
      }
      reset();
      onCreated();
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Log expense
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="exp-cat">Category</Label>
            <select
              id="exp-cat"
              value={category}
              onChange={(e) => setCategory(e.target.value as Category)}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="exp-amt">Amount (SAR)</Label>
            <Input
              id="exp-amt"
              autoFocus
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 125.50"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="exp-date">Date</Label>
            <Input
              id="exp-date"
              type="date"
              value={expenseDate}
              onChange={(e) => setExpenseDate(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="exp-desc" className="flex items-center gap-2">
              Description
              <span className="text-[10px] normal-case tracking-normal font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="exp-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. 4 grinder disks"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="exp-vendor" className="flex items-center gap-2">
              Vendor
              <span className="text-[10px] normal-case tracking-normal font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="exp-vendor"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder="Shop / store name"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Receipt photo</Label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) pickPhoto(f);
                e.target.value = '';
              }}
            />
            {photoPreview ? (
              <div className="flex items-center gap-3 rounded-md border border-border p-2">
                <img src={photoPreview} alt="" className="h-16 w-16 rounded object-cover bg-muted" />
                <div className="flex-1 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                  <Check className="h-3.5 w-3.5" /> Ready to upload
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={clearPhoto} aria-label="Remove photo">
                  <XIcon className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2 border-dashed h-20"
                onClick={() => fileRef.current?.click()}
              >
                <Camera className="h-5 w-5" />
                Take photo of receipt
              </Button>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm p-3">
              {error}
            </div>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }} disabled={busy}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={busy} className="gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Log expense
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
